import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { PatternProject } from "../core/types";
import { isIndexInSelection } from "../core/pattern";

export interface PatternCanvasHandle { fit: () => void; focusSelection: () => void; zoomBy: (factor: number) => void; }

interface PatternCanvasProps {
  project: PatternProject;
  onCellTap?: (index: number) => void;
  onCellLongPress?: (index: number) => void;
  className?: string;
  interactive?: boolean;
}

interface Viewport { scale: number; x: number; y: number; }
interface Point { x: number; y: number; startX: number; startY: number; }

const CELL = 24;
const HEADER = 26;

function contrastText(rgb: [number, number, number]): string {
  const luminance = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
  return luminance < 145 ? "#ffffff" : "#263241";
}

export const PatternCanvas = forwardRef<PatternCanvasHandle, PatternCanvasProps>(function PatternCanvas(
  { project, onCellTap, onCellLongPress, className = "", interactive = true }, ref
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewport = useRef<Viewport>({ scale: 1, x: 0, y: 0 });
  const pointers = useRef(new Map<number, Point>());
  const pinch = useRef<{ distance: number; scale: number; worldX: number; worldY: number } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const moved = useRef(false);
  const drawRef = useRef<() => void>(() => undefined);
  const projectRef = useRef(project);
  projectRef.current = project;
  const [size, setSize] = useState({ width: 1, height: 1 });
  const palette = useRef(new Map(project.palette.map((entry) => [entry.id, entry])));
  palette.current = new Map(project.palette.map((entry) => [entry.id, entry]));

  const fit = useCallback(() => {
    const contentWidth = HEADER + project.width * CELL;
    const contentHeight = HEADER + project.height * CELL;
    const scale = Math.min(size.width / contentWidth, size.height / contentHeight) * 0.96;
    viewport.current = {
      scale: Math.max(0.12, Math.min(4, scale)),
      x: (size.width - contentWidth * scale) / 2,
      y: (size.height - contentHeight * scale) / 2
    };
    drawRef.current();
  }, [project.height, project.width, size.height, size.width]);

  const zoomAround = useCallback((factor: number, screenX = size.width / 2, screenY = size.height / 2) => {
    const current = viewport.current;
    const nextScale = Math.max(0.12, Math.min(8, current.scale * factor));
    const worldX = (screenX - current.x) / current.scale;
    const worldY = (screenY - current.y) / current.scale;
    viewport.current = { scale: nextScale, x: screenX - worldX * nextScale, y: screenY - worldY * nextScale };
    drawRef.current();
  }, [size.height, size.width]);

  const focusSelection = useCallback(() => {
    const current = projectRef.current;
    let column = current.width / 2;
    let row = current.height / 2;
    if (current.currentMode === "row") row = current.currentSelection + .5;
    if (current.currentMode === "column") column = current.currentSelection + .5;
    if (current.currentMode === "block5") {
      const blockColumns = Math.ceil(current.width / 5);
      column = (current.currentSelection % blockColumns) * 5 + 2.5;
      row = Math.floor(current.currentSelection / blockColumns) * 5 + 2.5;
    }
    if (current.currentMode === "color" && current.selectedColorId) {
      const first = current.cells.findIndex((value) => value === current.selectedColorId);
      if (first >= 0) { column = first % current.width + .5; row = Math.floor(first / current.width) + .5; }
    }
    const scale = 1;
    const worldX = HEADER + column * CELL;
    const worldY = HEADER + row * CELL;
    viewport.current = { scale, x: size.width / 2 - worldX * scale, y: size.height * .42 - worldY * scale };
    drawRef.current();
  }, [size.height, size.width]);

  useImperativeHandle(ref, () => ({ fit, focusSelection, zoomBy: (factor) => zoomAround(factor) }), [fit, focusSelection, zoomAround]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const width = Math.max(1, Math.round(size.width * dpr));
    const height = Math.max(1, Math.round(size.height * dpr));
    if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
    const context = canvas.getContext("2d");
    if (!context) return;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, size.width, size.height);
    context.fillStyle = "#fff";
    context.fillRect(0, 0, size.width, size.height);

    const view = viewport.current;
    context.save();
    context.translate(view.x, view.y);
    context.scale(view.scale, view.scale);
    const worldLeft = Math.max(0, -view.x / view.scale);
    const worldTop = Math.max(0, -view.y / view.scale);
    const worldRight = (size.width - view.x) / view.scale;
    const worldBottom = (size.height - view.y) / view.scale;
    const startColumn = Math.max(0, Math.floor((worldLeft - HEADER) / CELL) - 1);
    const endColumn = Math.min(project.width, Math.ceil((worldRight - HEADER) / CELL) + 1);
    const startRow = Math.max(0, Math.floor((worldTop - HEADER) / CELL) - 1);
    const endRow = Math.min(project.height, Math.ceil((worldBottom - HEADER) / CELL) + 1);

    context.fillStyle = "#f6f7f9";
    context.fillRect(0, 0, HEADER + project.width * CELL, HEADER);
    context.fillRect(0, HEADER, HEADER, project.height * CELL);

    for (let row = startRow; row < endRow; row += 1) {
      for (let column = startColumn; column < endColumn; column += 1) {
        const index = row * project.width + column;
        const paletteId = project.cells[index] ?? 0;
        if (!paletteId) continue;
        const entry = palette.current.get(paletteId);
        if (!entry) continue;
        const selected = isIndexInSelection(project, index);
        const completed = project.completed[index] === 1;
        const x = HEADER + column * CELL;
        const y = HEADER + row * CELL;
        context.globalAlpha = selected ? 1 : 0.18;
        context.fillStyle = `rgb(${entry.displayRGB.join(",")})`;
        context.fillRect(x, y, CELL, CELL);
        context.globalAlpha = 1;
        if (completed) {
          context.fillStyle = "rgba(17,24,39,.34)";
          context.beginPath();
          context.arc(x + CELL - 5, y + 5, 2.2, 0, Math.PI * 2);
          context.fill();
        }
        if (CELL * view.scale >= 18) {
          context.globalAlpha = selected ? 1 : 0.36;
          context.fillStyle = contrastText(entry.displayRGB);
          context.font = "600 7px -apple-system, BlinkMacSystemFont, sans-serif";
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillText(entry.code, x + CELL / 2, y + CELL / 2 + .5);
          context.globalAlpha = 1;
        }
      }
    }

    context.lineCap = "butt";
    for (let column = startColumn; column <= endColumn; column += 1) {
      const x = HEADER + column * CELL;
      const major = column % 5 === 0;
      context.beginPath();
      context.strokeStyle = major ? "#7b8799" : "#dfe4eb";
      context.lineWidth = (major ? 1.1 : .55) / view.scale;
      context.moveTo(x, HEADER + startRow * CELL);
      context.lineTo(x, HEADER + endRow * CELL);
      context.stroke();
    }
    for (let row = startRow; row <= endRow; row += 1) {
      const y = HEADER + row * CELL;
      const major = row % 5 === 0;
      context.beginPath();
      context.strokeStyle = major ? "#7b8799" : "#dfe4eb";
      context.lineWidth = (major ? 1.1 : .55) / view.scale;
      context.moveTo(HEADER + startColumn * CELL, y);
      context.lineTo(HEADER + endColumn * CELL, y);
      context.stroke();
    }

    context.strokeStyle = "#3157E8";
    context.lineWidth = 2.5 / view.scale;
    if (project.currentMode === "row") {
      context.strokeRect(HEADER, HEADER + project.currentSelection * CELL, project.width * CELL, CELL);
    } else if (project.currentMode === "column") {
      context.strokeRect(HEADER + project.currentSelection * CELL, HEADER, CELL, project.height * CELL);
    } else if (project.currentMode === "block5") {
      const blockColumns = Math.ceil(project.width / 5);
      const blockRow = Math.floor(project.currentSelection / blockColumns);
      const blockColumn = project.currentSelection % blockColumns;
      const columns = Math.min(5, project.width - blockColumn * 5);
      const rows = Math.min(5, project.height - blockRow * 5);
      context.strokeRect(HEADER + blockColumn * 5 * CELL, HEADER + blockRow * 5 * CELL, columns * CELL, rows * CELL);
    }

    if (CELL * view.scale >= 13) {
      context.fillStyle = "#344054";
      context.font = "500 8px -apple-system, BlinkMacSystemFont, sans-serif";
      context.textAlign = "center";
      context.textBaseline = "middle";
      for (let column = startColumn; column < endColumn; column += 1) context.fillText(String(column + 1), HEADER + column * CELL + CELL / 2, HEADER / 2);
      for (let row = startRow; row < endRow; row += 1) context.fillText(String(row + 1), HEADER / 2, HEADER + row * CELL + CELL / 2);
    }
    context.restore();
  }, [project, size.height, size.width]);
  drawRef.current = draw;

  useEffect(() => { draw(); }, [draw]);
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => { if (size.width > 2 && size.height > 2) { if (interactive) focusSelection(); else fit(); } }, [fit, focusSelection, interactive, project.currentMode, project.currentSelection, project.id, project.selectedColorId, size.height, size.width]);

  function hitTest(clientX: number, clientY: number): number {
    const bounds = canvasRef.current!.getBoundingClientRect();
    const view = viewport.current;
    const worldX = (clientX - bounds.left - view.x) / view.scale;
    const worldY = (clientY - bounds.top - view.y) / view.scale;
    const column = Math.floor((worldX - HEADER) / CELL);
    const row = Math.floor((worldY - HEADER) / CELL);
    return row >= 0 && row < project.height && column >= 0 && column < project.width ? row * project.width + column : -1;
  }

  function pointerDown(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!interactive) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY, startX: event.clientX, startY: event.clientY });
    moved.current = false;
    if (pointers.current.size === 1) {
      const index = hitTest(event.clientX, event.clientY);
      if (index >= 0 && onCellLongPress) longPressTimer.current = setTimeout(() => { onCellLongPress(index); moved.current = true; }, 520);
    } else if (pointers.current.size === 2) {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      const [a, b] = [...pointers.current.values()];
      if (a && b) {
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const centerX = (a.x + b.x) / 2 - canvasRef.current!.getBoundingClientRect().left;
        const centerY = (a.y + b.y) / 2 - canvasRef.current!.getBoundingClientRect().top;
        pinch.current = { distance, scale: viewport.current.scale, worldX: (centerX - viewport.current.x) / viewport.current.scale, worldY: (centerY - viewport.current.y) / viewport.current.scale };
      }
    }
  }

  function pointerMove(event: React.PointerEvent<HTMLCanvasElement>) {
    const point = pointers.current.get(event.pointerId);
    if (!point) return;
    const previousX = point.x;
    const previousY = point.y;
    point.x = event.clientX;
    point.y = event.clientY;
    if (Math.hypot(point.x - point.startX, point.y - point.startY) > 5) {
      moved.current = true;
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    }
    if (pointers.current.size === 1) {
      viewport.current.x += point.x - previousX;
      viewport.current.y += point.y - previousY;
    } else if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      if (a && b) {
        const bounds = canvasRef.current!.getBoundingClientRect();
        const centerX = (a.x + b.x) / 2 - bounds.left;
        const centerY = (a.y + b.y) / 2 - bounds.top;
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const scale = Math.max(.12, Math.min(8, pinch.current.scale * distance / Math.max(1, pinch.current.distance)));
        viewport.current = { scale, x: centerX - pinch.current.worldX * scale, y: centerY - pinch.current.worldY * scale };
      }
    }
    draw();
  }

  function pointerUp(event: React.PointerEvent<HTMLCanvasElement>) {
    const point = pointers.current.get(event.pointerId);
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    if (point && pointers.current.size === 1 && !moved.current) {
      const index = hitTest(event.clientX, event.clientY);
      if (index >= 0) onCellTap?.(index);
    }
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  }

  return (
    <div ref={containerRef} className={`pattern-canvas ${className}`}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={`${project.width}列${project.height}行拼豆图纸，当前${project.currentMode}模式`}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={pointerUp}
        onDoubleClick={() => fit()}
        onWheel={(event) => { event.preventDefault(); zoomAround(event.deltaY < 0 ? 1.12 : .89, event.nativeEvent.offsetX, event.nativeEvent.offsetY); }}
      />
    </div>
  );
});
